import { View, Text } from "react-native";
import { webPrivacyProps } from "../../lib/privacy";

export function fmtAmt(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const m = value / 1_000_000;
    const s = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1);
    return `$${s}M`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function SectionDivider({ title }: { title: string }) {
  return (
    <View className="flex-row items-center gap-2 mt-5 mb-2">
      <Text className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {title}
      </Text>
      <View className="flex-1 h-px bg-gray-100 dark:bg-gray-700" />
    </View>
  );
}

export function LineItem({
  label,
  amount,
  amountColor,
  bold,
  note,
}: {
  label: string;
  amount: number;
  amountColor?: string;
  bold?: boolean;
  note?: string;
}) {
  const amtLabel = amount === 0 ? "—" : fmtAmt(Math.abs(amount));
  return (
    <View className="flex-row items-center justify-between py-1">
      <View className="flex-1 pr-2">
        <Text
          className={`text-sm ${bold ? "font-semibold" : "font-normal"} text-gray-700 dark:text-gray-300`}
          numberOfLines={1}
        >
          {label}
        </Text>
        {note && (
          <Text className="text-xs text-gray-400 dark:text-gray-500">
            {note}
          </Text>
        )}
      </View>
      <Text
        className={`text-sm ${bold ? "font-semibold" : "font-normal"} ${amountColor ?? "text-gray-800 dark:text-gray-200"}`}
        {...webPrivacyProps('financial')}
      >
        {amtLabel}
      </Text>
    </View>
  );
}

export function Subtotal({
  label,
  amount,
  color,
}: {
  label: string;
  amount: number;
  color?: string;
}) {
  return (
    <View className="flex-row items-center justify-between pt-2 mt-1 border-t border-gray-100 dark:border-gray-700">
      <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </Text>
      <Text
        className={`text-sm font-semibold ${color ?? "text-gray-800 dark:text-gray-200"}`}
        {...webPrivacyProps('financial')}
      >
        {fmtAmt(Math.abs(amount))}
      </Text>
    </View>
  );
}

export function PortfolioActivityRow({
  label,
  amount,
}: {
  label: string;
  amount: number;
}) {
  if (amount === 0) return null;
  const isContribution = amount < 0;
  return (
    <View className="flex-row items-center justify-between py-1">
      <Text className="text-sm text-gray-700 dark:text-gray-300 flex-1">
        {label}
      </Text>
      <View className="flex-row items-center gap-2">
        <Text
          className={`text-xs ${isContribution ? "text-green-600 dark:text-green-500" : "text-red-500 dark:text-red-400"}`}
        >
          {isContribution ? "Contributed" : "Drew"}
        </Text>
        <Text
          className={`text-sm font-medium ${isContribution ? "text-green-600 dark:text-green-500" : "text-red-500 dark:text-red-400"}`}
          {...webPrivacyProps('financial')}
        >
          {fmtAmt(Math.abs(amount))}
        </Text>
      </View>
    </View>
  );
}

export const GROUP_LABELS: Record<string, string> = {
  FOOD_AND_DRINK: "Food & Drink",
  ENTERTAINMENT: "Entertainment",
  GENERAL_MERCHANDISE: "Shopping",
  PERSONAL_CARE: "Personal Care",
  TRANSPORTATION: "Transportation",
  LOAN_PAYMENTS: "Loan Payments",
  RENT_AND_UTILITIES: "Rent & Utilities",
  HOME_IMPROVEMENT: "Home Improvement",
  MEDICAL: "Medical",
  GOVERNMENT_AND_NON_PROFIT: "Taxes & Charity",
  GENERAL_SERVICES: "Services",
  BANK_FEES: "Bank Fees",
  TRAVEL: "Travel",
};

export function groupLabel(group: string): string {
  return (
    GROUP_LABELS[group] ??
    group
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
